import {
  Css,
  HasIdAndName,
  MultiSelectField,
  NestedOption,
  SelectField,
  Switch,
  TreeSelectField,
} from "@homebound/beam";
import { useMemo } from "react";
import { Stage } from "src/generated/graphqlTypes";
import { DevelopmentBulkScheduleFilter } from "src/routes/developments/schedule/DevelopmentBulkSchedulePage";
import { DevelopmentBulkScheduleCohortDetailFragment } from "src/routes/developments/schedule/DevelopmentBulkSchedulePage.generated";
import { stageCodeToNameMapper } from "src/utils";
type DevelopmentBulkScheduleFilterModalProps = {
  filter: DevelopmentBulkScheduleFilter;
  setFilter: (filter: DevelopmentBulkScheduleFilter) => void;
  cohorts: DevelopmentBulkScheduleCohortDetailFragment[];
};

export enum DevelopmentBulkSchedulesGroupBy {
  None = "none",
  Cohort = "cohort",
}

export function DevelopmentBulkScheduleFilterModal({
  filter,
  setFilter,
  cohorts,
}: DevelopmentBulkScheduleFilterModalProps) {
  const stageOptions = [Stage.PreConExecution, Stage.Construction].map((s) => ({
    id: s,
    name: stageCodeToNameMapper[s],
  }));

  const treeOptions: NestedOption<HasIdAndName>[] = useMemo(
    () =>
      cohorts.map((c) => ({ id: c.id, name: c.name, children: c.projects?.map((p) => ({ id: p.id, name: p.name })) })),
    [cohorts],
  );

  // Combine cohort and project IDs for the TreeSelectField values
  const selectedValues = useMemo(
    () => [...(filter.cohort ?? []), ...(filter.project ?? [])],
    [filter.cohort, filter.project],
  );

  const groupByOptions: { id: DevelopmentBulkSchedulesGroupBy; name: string }[] = [
    { id: DevelopmentBulkSchedulesGroupBy.None, name: "None" },
    { id: DevelopmentBulkSchedulesGroupBy.Cohort, name: "Cohort" },
  ];

  return (
    <>
      <div css={Css.df.fdc.gap2.$}>
        <SelectField
          onSelect={(val) => {
            setFilter({ ...filter, groupBy: val! });
          }}
          label="Group By"
          options={groupByOptions}
          value={filter.groupBy}
        />
        <SelectField
          onSelect={(val) => {
            setFilter({ ...filter, stage: val! });
          }}
          label="Stage"
          options={stageOptions}
          value={filter.stage}
        />
        <TreeSelectField
          label="Projects"
          options={treeOptions}
          values={selectedValues}
          onSelect={({ root, leaf }) => {
            setFilter({
              ...filter,
              cohort: root.values.isEmpty ? undefined : (root.values as string[]),
              project: leaf.values.isEmpty ? undefined : (leaf.values as string[]),
            });
          }}
        />
        <MultiSelectField
          onSelect={(val) => setFilter({ ...filter, latestActiveStage: val.isEmpty ? undefined : val })}
          label="Current Stage"
          options={stageOptions}
          values={filter.latestActiveStage ?? []}
        />
        <Switch
          label="Milestones"
          selected={!!filter.milestonesOnly}
          onChange={(val) => {
            setFilter({ ...filter, milestonesOnly: val });
          }}
          labelStyle="filter"
          data-testid="milestonesOnly"
        />
        <Switch
          label="Show Task Status instead of Plan"
          selected={!!filter.taskStatusView}
          onChange={(val) => {
            setFilter({ ...filter, taskStatusView: val });
          }}
          labelStyle="filter"
          data-testid="taskStatusView"
        />
        <Switch
          selected={!!filter.stale}
          label="Stale Tasks"
          onChange={(val) => {
            setFilter({ ...filter, stale: val });
          }}
          labelStyle="filter"
          data-testid="stale"
        />
      </div>
    </>
  );
}
